/**
 * Skill games (wave A of docs/four-games-mobile-plan.md): the play-session and competition contract shared by
 * tile-match, color-sort, daily-words (pool competitions, catalog.ts) and chess (training against a bot; the pvp
 * tables of chess are wave C's own contract).
 *
 * PlaySession — one attempt at one challenge, saved on the server for a signed-in player (a guest keeps a local
 * save in the browser and imports it after signing in). The server never trusts the client's verdict: completion
 * re-plays the whole move journal through the game's validator (apps/server/src/skill/validators.ts) against the
 * challenge the server generated, checks the rules version and the limits below, and stores the result.
 *
 *   modes:   training — free practice: no stake, no reward, no XP, no passport stamp; the client may pick the seed.
 *            contest  — an entry of a prize-pool competition (`SkillContest`): the entry is an ordinary `Bet`
 *                       (ledger debit into the pool, responsible-play gate, idempotency key), the challenge is the
 *                       contest's, the same for every entrant; prizes are paid after the window closes.
 *            live     — reserved for chess games for a stake (wave C): a pvp table of its own, PlaySession optional.
 *   status:  active → completed (the replay solved it) | failed (the replay ended without a solution: a dead end
 *            the player gave up, a lost chess game) | abandoned (left; a contest entry stays in the pool).
 *
 * Concurrency: every write carries the session `version` the client last saw (optimistic lock). A stale write never
 * overwrites: the answer is `{ status: 'conflict', session }` with the server's copy — the client keeps both (shows
 * «continue the newer one» and may keep its own as a training session via import). Completion is idempotent: a
 * repeated call returns the stored result.
 *
 * HTTP (all under /api/skill, signed-in only, CSRF header, zod-validated, rate-limited):
 *   POST /sessions                       zSkillCreate        → SkillSessionView        (training)
 *   POST /sessions/import                zSkillImport        → SkillImportResult       (a local save after sign-in)
 *   GET  /sessions?game=&status=&limit=  zSkillListQuery     → SkillSessionList        («Continue», «Played»)
 *   GET  /sessions/:id                                       → SkillSessionView
 *   PUT  /sessions/:id                   zSkillSave          → SkillSaveResult
 *   POST /sessions/:id/complete          zSkillSave          → SkillCompleteResult     (save + replay + result)
 *   POST /sessions/:id/abandon           { version }         → SkillSaveResult
 *   GET  /contests?game=                                     → SkillContestList        (open windows + own recent)
 *   POST /contests/:id/enter             zSkillEnter         → SkillEnterResult        (Bet + contest session)
 *   GET  /contests/:id                                       → SkillContestDetail      (standings)
 * Socket (to the player's own room): `skill:contest` once a contest the player entered is settled or refunded.
 */
import { z } from 'zod';
import { zIdempotencyKey, zLocale, type Locale } from './common';
import { SKILL_GAME_IDS, PUZZLE_IDS, POOL_ENTRIES, POOL_FEE_BPS, type PuzzleId, type SkillGameId } from './catalog';

export const SKILL_MODES = ['training', 'contest', 'live'] as const;
export type SkillMode = (typeof SKILL_MODES)[number];

export const SKILL_STATUSES = ['active', 'completed', 'failed', 'abandoned'] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

/** Server-enforced limits of a session (a validator may lower `maxMoves` for its game). */
export const SKILL_LIMITS = {
  /** moves in one journal */
  maxMoves: 3000,
  /** one move as JSON, bytes */
  maxMoveBytes: 200,
  /** moves appended by one save */
  maxMovesPerSave: 300,
  /** the client's view state as JSON, bytes (UI convenience only: the truth is the journal) */
  maxStateBytes: 16_384,
  /** game parameters of a training session as JSON, bytes */
  maxParamsBytes: 512,
  /** active sessions of one player in one game (a new training session abandons the oldest one above it) */
  maxActivePerGame: 3,
  /** saves per minute per player (HTTP rate limit of PUT / complete) */
  savesPerMin: 60,
} as const;

/** A move of the journal: game-specific JSON (each validator has its own zod schema). */
export type SkillMove = string | number | boolean | null | SkillMove[] | { [k: string]: SkillMove };

const jsonBytes = (v: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(v) ?? '').length;
  } catch {
    return Infinity;
  }
};
const zJson = z.unknown();
const zBoundedJson = (max: number) => zJson.refine((v) => jsonBytes(v) <= max, { message: 'too large' });
const zMove = zBoundedJson(SKILL_LIMITS.maxMoveBytes);
const zParams = z
  .record(z.string().max(32), z.union([z.string().max(64), z.number(), z.boolean()]))
  .refine((v) => jsonBytes(v) <= SKILL_LIMITS.maxParamsBytes, { message: 'too large' });
export type SkillParams = z.infer<typeof zParams>;

/** A training seed: 16–64 hex chars (the client may pick it; the training challenge is generated from it) */
export const zSkillSeed = z.string().regex(/^[0-9a-f]{16,64}$/);

export const zSkillGame = z.enum(SKILL_GAME_IDS);
export const zPuzzleGame = z.enum(PUZZLE_IDS);

/** POST /api/skill/sessions — a training session */
export const zSkillCreate = z
  .object({
    game: zSkillGame,
    locale: zLocale,
    /** difficulty, layout, bot level … (validated by the game) */
    params: zParams.default({}),
    /** absent → the server picks a random seed */
    seed: zSkillSeed.optional(),
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export type SkillCreateInput = z.input<typeof zSkillCreate>;

/** PUT /api/skill/sessions/:id and POST …/complete: append moves `from` the server's count, replace the view state */
export const zSkillSave = z
  .object({
    /** the session version this write is based on */
    version: z.number().int().min(0),
    /** number of journal moves the client already had from the server (= index of the first new move) */
    from: z.number().int().min(0).max(SKILL_LIMITS.maxMoves),
    moves: z.array(zMove).max(SKILL_LIMITS.maxMovesPerSave).default([]),
    state: zBoundedJson(SKILL_LIMITS.maxStateBytes).optional(),
  })
  .strict();
export type SkillSaveInput = z.input<typeof zSkillSave>;

/** POST /api/skill/sessions/import — a guest's local training save, after signing in (idempotent by clientId) */
export const zSkillImport = z
  .object({
    game: zSkillGame,
    locale: zLocale,
    /** the local save's own id (uuid): the second import of the same save changes nothing or updates it by version */
    clientId: z.string().uuid(),
    seed: zSkillSeed,
    params: zParams.default({}),
    rulesVersion: z.number().int().min(1),
    /** local version (each local save +1): a newer local copy replaces an older server copy of the same clientId */
    version: z.number().int().min(0),
    moves: z.array(zMove).max(SKILL_LIMITS.maxMoves),
    state: zBoundedJson(SKILL_LIMITS.maxStateBytes).optional(),
    /** the local save finished: the server re-plays and stores the result (training: no reward) */
    completed: z.boolean().default(false),
    startedAt: z.string().datetime(),
  })
  .strict();
export type SkillImportInput = z.input<typeof zSkillImport>;

export const zSkillListQuery = z.object({
  game: zSkillGame.optional(),
  status: z.enum(['active', 'finished']).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

/** The server's verdict of a re-played journal. */
export interface SkillResultView {
  solved: boolean;
  /** game-specific numbers shown to the player (moves, hints, words …) */
  metrics: Record<string, number>;
  /** server time from the start of the session to the completion, ms */
  elapsedMs: number;
}

export interface SkillSessionView {
  id: string;
  game: SkillGameId;
  mode: SkillMode;
  locale: Locale;
  /** `t:<seed>` (training), `c:<contestId>` (contest) — what makes two sessions «the same task» */
  challengeId: string;
  rulesVersion: number;
  /** the challenge as the game generated it (layout, tubes, letters, start position …) */
  challenge: unknown;
  params: SkillParams;
  moves: unknown[];
  state: unknown;
  status: SkillStatus;
  version: number;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
  result: SkillResultView | null;
  contestId: string | null;
  /** the entry bet of a contest session */
  betId: string | null;
  clientId: string | null;
}

/** list rows: the same without the bulky journal / challenge / state */
export type SkillSessionRow = Omit<SkillSessionView, 'moves' | 'challenge' | 'state'> & { moveCount: number };

export interface SkillSessionList {
  sessions: SkillSessionRow[];
}

export type SkillSaveResult =
  | { status: 'saved'; version: number; moveCount: number }
  /** the write was stale: nothing changed, here is the server's copy */
  | { status: 'conflict'; session: SkillSessionView };

export type SkillCompleteResult =
  | { status: 'completed' | 'failed'; session: SkillSessionView }
  | { status: 'conflict'; session: SkillSessionView };

export interface SkillImportResult {
  /** created — a new server session; updated — the local copy was newer; kept — the server copy is newer or finished */
  status: 'created' | 'updated' | 'kept';
  session: SkillSessionView;
}

/* ---------------- prize-pool competitions ---------------- */

export const SKILL_PERIODS = ['hour', 'day'] as const;
export type SkillPeriod = (typeof SKILL_PERIODS)[number];

export interface SkillContestConfig {
  /** windows of the competition (UTC hour / UTC day) */
  periods: readonly SkillPeriod[];
  /** entry fees offered: a contest per (window, entry, locale) */
  entries: readonly number[];
  feeBps: number;
  /** fewer entrants than this → every entry is refunded (one entrant alone always is) */
  minEntries: number;
  /** a solution may still be submitted this long after the window closes (entries close with the window) */
  graceMs: number;
  /** one contest per locale (daily-words: own dictionaries; rankings of languages never mix) */
  perLocale: boolean;
  /** one entry per player per window across locales and entries (no second reward by switching the language) */
  onePerWindow: boolean;
}

const HOURLY = { periods: ['hour'], entries: POOL_ENTRIES, feeBps: POOL_FEE_BPS, minEntries: 2, graceMs: 10 * 60_000, perLocale: false, onePerWindow: false } as const;

/** Competition rules per puzzle (owner decision 2026-10-01). */
export const SKILL_CONTEST_CONFIG: Record<PuzzleId, SkillContestConfig> = {
  'tile-match': HOURLY,
  'color-sort': HOURLY,
  'daily-words': {
    periods: ['day'],
    entries: POOL_ENTRIES,
    feeBps: POOL_FEE_BPS,
    minEntries: 2,
    graceMs: 30 * 60_000,
    perLocale: true,
    onePerWindow: true,
  },
};

/** paid places of a competition with `n` entrants: ≈ the top 30 %, at least one */
export const contestPaidPlaces = (n: number): number => Math.max(1, Math.round(n * 0.3));

export type SkillContestStatus = 'open' | 'closed' | 'settled' | 'refunded';

export interface SkillContestEntryView {
  sessionId: string;
  status: SkillStatus;
  /** 1-based place among the finishers (ties share it); null — not finished / not ranked yet */
  rank: number | null;
  /** tokens credited at settlement (prize or refund); null before */
  payout: number | null;
}

export interface SkillContestView {
  id: string;
  game: PuzzleId;
  period: SkillPeriod;
  /** null: one contest for every language */
  locale: Locale | null;
  entry: number;
  feeBps: number;
  minEntries: number;
  opensAt: string;
  /** entries close */
  closesAt: string;
  /** solutions close, prizes are paid */
  settleAt: string;
  status: SkillContestStatus;
  entries: number;
  /** Σ entries − fee (what the prizes will add up to at most) */
  fund: number;
  paidPlaces: number;
  /** sha256 of the challenge seed, published at the opening */
  seedHash: string;
  /** the seed, revealed after the settlement */
  seed: string | null;
  me: SkillContestEntryView | null;
}

export interface SkillContestList {
  /** windows open now for the game (each entry fee, the player's locale where it matters) */
  open: SkillContestView[];
  /** the player's latest contests of the game, newest first */
  mine: SkillContestView[];
}

export interface SkillStandingRow {
  rank: number;
  nickname: string;
  avatar: string;
  elapsedMs: number;
  metrics: Record<string, number>;
  payout: number | null;
  me: boolean;
}

export interface SkillContestDetail {
  contest: SkillContestView;
  /** finishers in rank order (≤ 100) */
  standings: SkillStandingRow[];
}

export const zSkillEnter = z.object({ idempotencyKey: zIdempotencyKey }).strict();

export interface SkillEnterResult {
  session: SkillSessionView;
  contest: SkillContestView;
  balance: number;
  /** this idempotency key was used before: the original entry, nothing debited now */
  duplicate: boolean;
}

/** `skill:contest` — a contest the player entered was settled or refunded */
export interface SkillContestEvent {
  contest: SkillContestView;
}

export interface SkillServerToClient {
  'skill:contest': (e: SkillContestEvent) => void;
}

/* ---------------- product analytics of skill games (cookie-less, analytics.ts) ---------------- */

/**
 * Funnel events of the skill games (POST /api/a/x): card seen → start → finish / exit → return / restore, hints,
 * time to the first action, control errors. Counted per (day, game, mode, event) without any user id.
 */
export const SKILL_AN_EVENTS = ['card', 'start', 'finish', 'exit', 'restore', 'hint', 'first', 'error'] as const;
export type SkillAnEvent = (typeof SKILL_AN_EVENTS)[number];
/** analytics mode: the session mode, or the chess game kind */
export const SKILL_AN_MODES = ['training', 'contest', 'bot', 'live', 'none'] as const;
export type SkillAnMode = (typeof SKILL_AN_MODES)[number];

export const zSkillAnBeacon = z
  .object({
    /** event */
    e: z.enum(SKILL_AN_EVENTS),
    g: zSkillGame,
    m: z.enum(SKILL_AN_MODES).default('none'),
    /** `first`: ms from the start to the first action; `finish`/`exit`: seconds played (clamped) */
    v: z.number().int().min(0).max(86_400_000).optional(),
  })
  .strict();
export type SkillAnBeacon = z.input<typeof zSkillAnBeacon>;

/** GET /api/admin/analytics/skill?from=&to= → { rows } */
export interface AnSkillRow {
  game: string;
  mode: string;
  ev: string;
  n: number;
  /** average value of the events that carried one (ms to the first action / seconds played); null — none */
  avg: number | null;
}
